import './App.css';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from './dashboardheader.js'
import VerticalNavigation from './VerticalNavigation.js'
import bhcalc from './bhcalc.js'

const emptyForm = { name: '', address: '', zipcode: '', logoUrl: '' };

function Locations() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editingLocation, setEditingLocation] = useState(null);
  const [editFormData, setEditFormData] = useState(emptyForm);
  const [editLogoFile, setEditLogoFile] = useState(null);
  const [editLogoPreview, setEditLogoPreview] = useState(null);
  const [editError, setEditError] = useState('');
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  const [deletingLocation, setDeletingLocation] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const fetchLocations = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await bhcalc.get('/location');
      setLocations(data);
    } catch (err) {
      console.error('Error fetching locations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setLogoFile(null);
      setLogoPreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('address', formData.address);
      fd.append('zipcode', formData.zipcode);
      if (logoFile) fd.append('logo', logoFile);

      await bhcalc.post('/location', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setShowAddModal(false);
      setFormData(emptyForm);
      setLogoFile(null);
      setLogoPreview(null);
      fetchLocations();
    } catch (err) {
      console.error('Error creating location:', err);
      if (err.response?.status === 400) setFormError('Invalid location data. Please check your inputs.');
      else if (err.response?.status === 401) setFormError('Session expired. Please log in again.');
      else if (err.response?.status === 403) setFormError('You do not have permission to create locations.');
      else if (err.response?.status === 500) setFormError('Server error. Please try again later.');
      else setFormError('Unable to create location. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowAddModal(false);
    setFormData(emptyForm);
    setLogoPreview(null);
    setFormError('');
  };

  const openEdit = (location) => {
    setEditingLocation(location);
    setEditFormData({
      name: location.name || '',
      address: location.address || '',
      zipcode: location.zipcode || ''
    });
    setEditLogoFile(null);
    setEditLogoPreview(null);
    setEditError('');
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setEditLogoPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setEditLogoFile(null);
      setEditLogoPreview(null);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditError('');
    setIsEditSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', editFormData.name);
      fd.append('address', editFormData.address);
      fd.append('zipcode', editFormData.zipcode);
      if (editLogoFile) fd.append('logo', editLogoFile);

      await bhcalc.put(`/location/${editingLocation.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setEditingLocation(null);
      fetchLocations();
    } catch (err) {
      console.error('Error updating location:', err);
      if (err.response?.status === 400) setEditError('Invalid location data. Please check your inputs.');
      else if (err.response?.status === 401) setEditError('Session expired. Please log in again.');
      else if (err.response?.status === 403) setEditError('You do not have permission to edit locations.');
      else if (err.response?.status === 404) setEditError('Location not found.');
      else if (err.response?.status === 500) setEditError('Server error. Please try again later.');
      else setEditError('Unable to update location. Please try again.');
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleEditCancel = () => {
    setEditingLocation(null);
    setEditError('');
  };

  const openDelete = (location) => {
    setDeletingLocation(location);
    setDeleteError('');
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    setDeleteError('');
    try {
      await bhcalc.delete(`/location/${deletingLocation.id}`);
      setDeletingLocation(null);
      fetchLocations();
    } catch (err) {
      console.error('Error deleting location:', err);
      if (err.response?.status === 401) setDeleteError('Session expired. Please log in again.');
      else if (err.response?.status === 403) setDeleteError('You do not have permission to delete locations.');
      else if (err.response?.status === 404) setDeleteError('Location not found.');
      else if (err.response?.status === 500) setDeleteError('Server error. Please try again later.');
      else setDeleteError('Unable to delete location. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="App min-h-screen">
      <div className="dashboard-wrapper flex flex-col min-h-screen">
        <div className="dashboard-header flex w-full max-h-24 h-14">
          <DashboardHeader />
        </div>
        <div className="w-full flex flex-wrap min-h-screen">
          <div className="dashboard-left-sidebar flex-none w-48 relative">
            <VerticalNavigation />
          </div>
          <div className="dashboard-right-main border-1 flex-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-left">Locations</h2>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-3 py-1 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
              >
                <span className="text-xl">+</span>
                <span>Add Location</span>
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Address</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Zipcode</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Logo</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-left">
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">Loading locations...</td>
                    </tr>
                  ) : locations.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">No locations found. Add your first location above.</td>
                    </tr>
                  ) : (
                    locations.map((location) => (
                      <tr key={location.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{location.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{location.address}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{location.zipcode}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {location.logoUrl
                            ? <img src={location.logoUrl} alt={location.name} className="h-8 w-8 object-cover rounded" />
                            : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => navigate(`/location/${location.id}`)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            Manage
                          </button>
                          <button
                            onClick={() => openEdit(location)}
                            className="text-indigo-600 hover:text-indigo-900 mr-3"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => openDelete(location)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {locations.length > 0 && (
              <div className="flex justify-between items-center mt-4">
                <span className="text-sm text-gray-600">Showing {locations.length} location{locations.length !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed text-left inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add New Location</h2>
                <button
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                {formError && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{formError}</div>
                )}
                <LocationFormFields
                  data={formData}
                  onChange={handleChange}
                  onFileChange={handleFileChange}
                  logoPreview={logoPreview}
                />
                <div className="flex justify-end space-x-3 pt-4 border-t mt-6">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Adding...' : 'Add Location'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingLocation && (
        <div className="fixed text-left inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Location</h2>
                <button
                  onClick={handleEditCancel}
                  disabled={isEditSubmitting}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleEditSubmit}>
                {editError && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{editError}</div>
                )}
                <LocationFormFields
                  data={editFormData}
                  onChange={handleEditChange}
                  onFileChange={handleEditFileChange}
                  logoPreview={editLogoPreview}
                />
                <div className="flex justify-end space-x-3 pt-4 border-t mt-6">
                  <button
                    type="button"
                    onClick={handleEditCancel}
                    disabled={isEditSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isEditSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isEditSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingLocation && (
        <div className="fixed text-left inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Delete Location</h2>
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete <span className="font-semibold">{deletingLocation.name}</span>? This action cannot be undone.
              </p>
              {deleteError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{deleteError}</div>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeletingLocation(null)}
                  disabled={isDeleting}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LocationFormFields({ data, onChange, onFileChange, logoPreview }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Location Name *</label>
        <input
          type="text"
          name="name"
          value={data.name}
          onChange={onChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter location name"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
        <input
          type="text"
          name="address"
          value={data.address}
          onChange={onChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter street address"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Zipcode *</label>
        <input
          type="text"
          name="zipcode"
          value={data.zipcode}
          onChange={onChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter zipcode"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Upload Logo Image</label>
        <input
          type="file"
          accept="image/*"
          onChange={onFileChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        />
        {logoPreview && (
          <div className="mt-2">
            <img src={logoPreview} alt="Logo preview" className="max-h-32 rounded-md border border-gray-200" />
          </div>
        )}
      </div>
    </div>
  );
}

export default Locations;
